import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import debounce from '../utils/debounce';

import CollapsibleDown from '../icons/CollapsibleDown';

import CollapsibleUp from '../icons/CollapsibleUp';

import styles from './Collapsible.module.scss';

const getStateFromProps = props => ({
  collapsed: props.isCollapsed,
  height: props.isCollapsed ? '0px' : 'auto'
});

class Collapsible extends React.PureComponent {
  state = getStateFromProps(this.props);

  collapsibleBodyref = React.createRef();
  collapsibleTimeout = null;

  updateHeight = debounce(() => {
    const newHeight = this.getContentHeight();
    if (!this.state.collapsed && this.state.height !== newHeight) {
      this.setState({ height: this.getContentHeight() });
    }
  }, 150);

  componentDidMount = () => {
    window.addEventListener('resize', this.updateHeight);
  };

  componentWillReceiveProps = nextProps => {
    if (
      nextProps.isCollapsed !== this.state.collapsed &&
      this.props.isControlled
    ) {
      if (nextProps.isCollapsed) {
        this.handleClose();
      } else {
        this.handleOpen();
      }
    }
  };

  componentWillUnmount = () => {
    this.updateHeight.cancel();
    window.removeEventListener('resize', this.updateHeight);
  };

  handleOpen = () => {
    this.setState({
      collapsed: false,
      height: this.getContentHeight(),
      isTransitioning: true
    });
  };

  handleClose = () => {
    this.updateRAFState(
      { height: this.getContentHeight(), collapsed: false },
      () => {
        this.updateRAFState({ collapsed: true, height: 0 });
      }
    );
  };

  handleClick = e => {
    e.preventDefault();
    if (this.state.collapsed) {
      this.handleOpen();
      this.props.onCollapsedChange(false);
    } else {
      this.handleClose();
      this.props.onCollapsedChange(true);
    }
  };
  handleTransitionEnd = () => {
    this.setState({ isTransitioning: false });
  };

  getContentHeight = () => {
    const content = this.collapsibleBodyref.current;
    const contentStyles = window.getComputedStyle(content);
    const absoluteContentHeight =
      parseInt(contentStyles.height, 10) +
      parseInt(contentStyles.marginTop, 10) +
      parseInt(contentStyles.marginBottom, 10);

    return `${absoluteContentHeight}px`;
  };

  updateRAFState = (newState, cb = () => {}) => {
    requestAnimationFrame(() => {
      this.setState(newState, cb);
    });
  };

  render() {
    const { height, collapsed, isTransitioning } = this.state;
    const wrapperStyles = { minHeight: height };

    const collapsibleClassNames = classNames(this.props.className, {
      'collapsible--collapsed': collapsed,
      'collapsible--inline': this.props.inlineTitle
    });

    const headerClassNames = classNames(styles.collapsible__header, {
      [this.props.headerClassName]: this.props.headerClassName,
      [styles['collapsible__header--rounded']]: this.props.isRounded,
      [styles['collapsible__header--rounded-open']]:
        this.props.isRounded && this.state.isCollapsed
    });

    const wrapperClassNames = classNames(
      styles['collapsible__content-wrapper'],
      {
        [styles['collapsible__content-wrapper--opened']]:
          !collapsed && !isTransitioning,
        [styles['collapsible__content-wrapper--rounded']]:
          this.props.isRounded && !this.state.isCollapsed
      }
    );

    const bodyClassNames = classNames(this.props.bodyClassName, {
      [styles['collapsible__content-body--rounded']]: this.props.isRounded
    });

    return (
      <div className={collapsibleClassNames}>
        <div
          className={headerClassNames}
          onClick={this.handleClick}
          role="presentation"
        >
          {this.props.renderHeader(this.state.collapsed)}
          {this.props.hasIcon &&
            (collapsed ? (
              <CollapsibleDown theme={this.props.iconTheme} />
            ) : (
              <CollapsibleUp theme={this.props.iconTheme} />
            ))}
        </div>
        <div
          style={wrapperStyles}
          className={wrapperClassNames}
          onTransitionEnd={this.handleTransitionEnd}
        >
          <div className={bodyClassNames} ref={this.collapsibleBodyref}>
            {this.props.renderBody(this.state.collapsed)}
          </div>
        </div>
      </div>
    );
  }
}

Collapsible.propTypes = {
  renderHeader: PropTypes.func.isRequired,
  renderBody: PropTypes.func.isRequired,
  className: PropTypes.string,
  hasIcon: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onCollapsedChange: PropTypes.func,
  inlineTitle: PropTypes.bool,
  bodyClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  isRounded: PropTypes.bool,
  iconTheme: PropTypes.string,
  isControlled: PropTypes.bool
};

Collapsible.defaultProps = {
  className: '',
  hasIcon: false,
  isCollapsed: false,
  onCollapsedChange: () => {},
  inlineTitle: false,
  bodyClassName: '',
  headerClassName: '',
  isRounded: false,
  iconTheme: 'dark',
  isControlled: true
};

export default Collapsible;
